import { Role, User } from '../types';

export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Admin',
  super_admin: 'Super Admin',
  integrator: 'Dealer',
  dealer: 'Dealer',
  customer: 'Customer',
  property_manager: 'Property Manager',
  technician: 'Technician',
  viewer: 'Viewer',
  tenant: 'Tenant',
};

export const ROLE_CATALOG = [
  { value: 'super_admin', label: 'Super Admin', aliases: [] },
  { value: 'admin', label: 'Admin', aliases: [] },
  { value: 'integrator', label: 'Dealer', aliases: ['dealer', 'dealer_admin', 'dealer_user'] },
  { value: 'customer', label: 'Customer', aliases: ['customer_admin', 'customer_user'] },
  { value: 'property_manager', label: 'Property Manager', aliases: [] },
  { value: 'technician', label: 'Technician', aliases: [] },
  { value: 'viewer', label: 'Viewer', aliases: [] },
  { value: 'tenant', label: 'Tenant', aliases: [] },
] as const;

export const normalizeRole = (role: unknown): string => {
  const candidate = String(role || '').trim().toLowerCase();
  const match = ROLE_CATALOG.find(item => item.value === candidate || item.aliases.some(alias => alias === candidate));
  return match?.value || candidate;
};

export const roleDisplayLabel = (role: unknown): string => {
  const normalized = normalizeRole(role);
  return ROLE_CATALOG.find(item => item.value === normalized)?.label || String(role || 'User').replace(/_/g, ' ');
};

export const ROLE_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  ...ROLE_CATALOG.map(({ value, label }) => ({ value, label })),
];

export const isSuperAdmin = (role: Role) => normalizeRole(role) === 'super_admin';
export const isAdmin = (role: Role) => normalizeRole(role) === 'admin' || isSuperAdmin(role);
export const isDealer = (role: Role) => normalizeRole(role) === 'integrator';
export const isIntegrator = (role: Role) => isDealer(role);
export const isCustomer = (role: Role) => normalizeRole(role) === 'customer';
export const isPropertyManager = (role: Role) => normalizeRole(role) === 'property_manager';
export const isTechnician = (role: Role) => normalizeRole(role) === 'technician';
export const isViewer = (role: Role) => normalizeRole(role) === 'viewer';
export const isTenant = (role: Role) => normalizeRole(role) === 'tenant';

export const canViewIntegrators = (role: Role) => isAdmin(role);
export const canViewCustomers = (role: Role) => isAdmin(role) || isDealer(role);
export const canViewProperties = (role: Role) => isAdmin(role) || isDealer(role) || isCustomer(role) || isPropertyManager(role);
export const canViewTenants = (role: Role) => isAdmin(role) || isDealer(role) || isCustomer(role) || isPropertyManager(role);
export const canViewDevices = (role: Role) => isAdmin(role) || isDealer(role) || isCustomer(role);
export const canViewAccessControl = (role: Role) => isAdmin(role) || isDealer(role) || isCustomer(role) || isPropertyManager(role);
export const canAddDevices = (role: Role) => isAdmin(role) || isDealer(role) || isCustomer(role) || isPropertyManager(role);
export const canViewBilling = (role: Role) => isAdmin(role) || isDealer(role);
export const canViewVoice = (role: Role) => isAdmin(role) || isDealer(role);
export const canViewSettings = (role: Role) => isAdmin(role) || isDealer(role) || isCustomer(role) || isPropertyManager(role);
export const canViewWebUsers = (role: Role) => isAdmin(role);
export const canViewMobileUsers = (role: Role) => isAdmin(role);
export const canViewCallHistory = (role: Role) => isAdmin(role) || isDealer(role) || isCustomer(role) || isPropertyManager(role);
export const canViewGuests = (role: Role) =>
  isAdmin(role) || isDealer(role) || isCustomer(role) || isPropertyManager(role);
export const canViewBillingRates = (role: Role) => isSuperAdmin(role);
export const canViewCameras = (role: Role) => isAdmin(role) || isDealer(role) || isCustomer(role);
export const canViewIntercoms = (role: Role) => isAdmin(role) || isDealer(role) || isCustomer(role);
export const canViewPermissions = (role: Role) => isSuperAdmin(role);
export const canAccessSignShop = (role: Role, user?: User) => isAdmin(role) || user?.sign_shop_access === true;

export interface NavItem {
  label: string;
  path: string;
  icon: string;
  check: (role: Role, user?: User) => boolean;
  section?: string;
}

export interface HeaderNavGroup {
  label: string;
  path?: string; // Direct link (no dropdown) if set
  items?: NavItem[];
  columns?: { label: string; items: NavItem[] }[];
}

export const HEADER_NAV_GROUPS: HeaderNavGroup[] = [
  {
    label: 'Dashboard',
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: 'home', check: () => true },
      // Notifications entry removed from the sidebar. The /notifications
      // route stays mounted in App.tsx for now (existing deep links don't
      // 404) but no nav surfaces it.
      // Analytics lives only on admin.intrlock.io now (ADMIN_NAV_GROUPS).
    ],
  },
  {
    label: 'Organization',
    items: [
      { label: 'Dealers',    path: '/integrators', icon: 'users',     check: canViewIntegrators },
      { label: 'Customers',  path: '/customers',   icon: 'building',  check: canViewCustomers },
      { label: 'Properties', path: '/properties',  icon: 'map-pin',   check: canViewProperties },
      { label: 'People',     path: '/tenants',     icon: 'user',      check: canViewTenants },
      { label: 'Deliveries', path: '/deliveries',  icon: 'guest-key', check: canViewGuests },
      { label: 'Vendors',    path: '/vendors',     icon: 'users',     check: canViewGuests },
      { label: 'Guests',     path: '/guests',      icon: 'guest-key', check: canViewGuests },
      { label: 'Web Users',  path: '/web-users',   icon: 'user-cog',  check: canViewWebUsers },
    ],
  },
  {
    label: 'System',
    items: [
      { label: 'Intercoms',      path: '/intercoms',      icon: 'camera',    check: canViewIntercoms },
      { label: 'Devices',        path: '/devices',        icon: 'cpu',       check: canViewDevices },
      { label: 'Access Control', path: '/access-control', icon: 'shield',    check: canViewAccessControl },
      { label: 'Cameras',        path: '/cameras',        icon: 'camera',    check: canViewCameras },
      { label: 'Mobile App',     path: '/mobile-users',   icon: 'smartphone',check: canViewMobileUsers },
      { label: 'Permissions',    path: '/permissions',    icon: 'shield',    check: (role) => canViewPermissions(role) && !isSuperAdmin(role) },
      { label: 'Rules',          path: '/rules',          icon: 'settings',  check: canViewAccessControl },
    ],
  },
  // Supplies dropdown hidden until Sign Shop / Accessories ship —
  // currently lives on admin.intrlock.io only.
  {
    label: 'History',
    items: [
      { label: 'Call History',   path: '/call-history',   icon: 'clock', check: canViewCallHistory },
      { label: 'Device History', path: '/device-history', icon: 'clock', check: () => true },
      { label: 'Reports',        path: '/reports',        icon: 'clock', check: () => true },
    ],
  },
  {
    label: 'Settings',
    items: [
      { label: 'Account Settings', path: '/settings', icon: 'settings', check: canViewSettings },
      // All billing UI lives on admin.intrlock.io now — Billing Center,
      // Subscriptions, Invoices, money stats. Nothing money-related shows
      // on app.intrlock.io regardless of role.
    ],
  },
];

/// Super-admin nav, mounted only by the admin SPA at admin.intrlock.io.
/// Grouped into categorized dropdowns so the header stays readable as we
/// add admin-only surfaces. Mirrors the web-app HEADER_NAV_GROUPS shape so
/// HeaderNav renders both the same way.
const adminCheck = (role: Role) => isSuperAdmin(role);
export const ADMIN_NAV_GROUPS: HeaderNavGroup[] = [
  { label: 'Dashboard', path: '/admin' },
  {
    label: 'Organization',
    items: [
      { label: 'Dealers',      path: '/integrators',  icon: 'users',     check: adminCheck },
      { label: 'Customers',    path: '/customers',    icon: 'building',  check: adminCheck },
      { label: 'Properties',   path: '/properties',   icon: 'map-pin',   check: adminCheck },
      { label: 'People',       path: '/tenants',      icon: 'user',      check: adminCheck },
      { label: 'Deliveries',   path: '/deliveries',   icon: 'guest-key', check: adminCheck },
      { label: 'Vendors',      path: '/vendors',      icon: 'users',     check: adminCheck },
      { label: 'Guests',       path: '/guests',       icon: 'guest-key', check: adminCheck },
      { label: 'Web Users',    path: '/web-users',    icon: 'user-cog',  check: adminCheck },
      { label: 'Mobile App',   path: '/mobile-users', icon: 'smartphone',check: adminCheck },
    ],
  },
  {
    label: 'System',
    items: [
      { label: 'Intercoms',      path: '/intercoms',      icon: 'camera',   check: adminCheck },
      { label: 'Devices',        path: '/devices',        icon: 'cpu',      check: adminCheck },
      { label: 'Access Control', path: '/access-control', icon: 'shield',   check: adminCheck },
      { label: 'Cameras',        path: '/cameras',        icon: 'camera',   check: adminCheck },
      { label: 'Bridges',        path: '/bridges',        icon: 'cpu',      check: adminCheck },
      { label: 'Rules',          path: '/rules',          icon: 'settings', check: adminCheck },
    ],
  },
  {
    label: 'Integrations',
    items: [
      { label: 'Add PDK System', path: '/admin/add-pdk', icon: 'shield', check: adminCheck },
    ],
  },
  {
    label: 'History',
    items: [
      { label: 'Call History',   path: '/call-history',   icon: 'clock', check: adminCheck },
      { label: 'Device History', path: '/device-history', icon: 'clock', check: adminCheck },
      { label: 'Event History',  path: '/event-history',  icon: 'clock', check: adminCheck },
      { label: 'Reports',        path: '/reports',        icon: 'clock', check: adminCheck },
    ],
  },
  {
    label: 'Operations',
    items: [
      { label: 'Operations', path: '/operations',         icon: 'chart', check: adminCheck },
      { label: 'Metrics',    path: '/operations/metrics', icon: 'chart', check: adminCheck },
      { label: 'Analytics',  path: '/analytics',          icon: 'chart', check: adminCheck },
    ],
  },
  {
    label: 'Money',
    items: [
      { label: 'Billing',       path: '/billing',              icon: 'dollar', check: adminCheck },
      { label: 'Billing Rates', path: '/admin/billing-rates',  icon: 'dollar', check: adminCheck },
      { label: 'Stripe',        path: '/admin/stripe',         icon: 'dollar', check: adminCheck },
    ],
  },
  {
    label: 'Communication',
    items: [
      { label: 'Inbox',     path: '/admin/inbox',    icon: 'mail',       check: adminCheck },
      { label: 'Send Push', path: '/push-sender',    icon: 'send',       check: adminCheck },
      { label: 'Cellular',  path: '/admin/cellular', icon: 'smartphone', check: adminCheck },
    ],
  },
  {
    label: 'Admin',
    items: [
      { label: 'Permissions',  path: '/permissions',        icon: 'shield', check: adminCheck },
      { label: 'Data',         path: '/admin/data',         icon: 'cpu',    check: adminCheck },
      { label: 'Live Visitor', path: '/admin/live-visitor', icon: 'users',  check: adminCheck },
      { label: 'Beta Visitor', path: '/admin/beta-visitor', icon: 'users',  check: adminCheck },
    ],
  },
  {
    label: 'Supplies',
    items: [
      { label: 'Sign Shop',              path: '/sign-shop',             icon: 'building', check: canAccessSignShop },
      { label: 'Sign Shop Orders',       path: '/sign-shop-orders',      icon: 'building', check: canAccessSignShop },
      { label: 'Vistaprint Shop',        path: '/vistaprint-shop',       icon: 'building', check: adminCheck },
      { label: 'Accessories',            path: '/accessories',           icon: 'building', check: adminCheck },
      { label: 'Accessory Shop Orders',  path: '/accessory-shop-orders', icon: 'building', check: adminCheck },
      { label: 'Device Profiles',        path: '/device-profiles',       icon: 'cpu',      check: adminCheck },
    ],
  },
];
